/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useMemo } from 'react';

import { MultiSelect } from '@coursera/cds-core';

import { NUMBER_OF_ITEMS_TO_FIT_IN_FILTER_DROPDOWN } from 'bundles/search-common/constants/filter';
import type { SearchFilterOption } from 'bundles/search-common/types';

import _t from 'i18n!nls/enterprise-collections';

const styles = {
  wrapper: css`
    & .cds-multiSelect-label,
    & .cds-selectController-label {
      color: var(--cds-color-neutral-primary);
      font-weight: var(--cds-font-weight-600);
    }
  `,
};

export type Props = {
  updateFilterAvailability: (attribute: string, filtersIsAvailable: boolean) => void;
  filterName: string;
  attribute: string;
  /** Add one or more filters; pass an array to apply multiple in a single update (recommended for multi-select). */
  addFilters?: (filters: string | string[]) => void;
  removeFilters?: (filters: string[]) => void;
  currentRefinement?: string | string[];
  items: Array<SearchFilterOption & { value?: string }>;
};

const SearchFilterMultiSelect = (props: Props) => {
  const { items, attribute, filterName, addFilters, removeFilters, currentRefinement, updateFilterAvailability } =
    props;

  const { Option } = MultiSelect;

  const sortedItems = useMemo(() => items.map((item) => ({ ...item, value: item.value ?? item.label })), [items]);

  const selectedValues = useMemo(() => {
    let refinements: string[];
    if (currentRefinement == null) {
      refinements = [];
    } else if (Array.isArray(currentRefinement)) {
      refinements = currentRefinement;
    } else {
      refinements = [currentRefinement];
    }
    return refinements.map((r) => r.split(':')[1]).filter(Boolean) as string[];
  }, [currentRefinement]);

  useEffect(() => {
    updateFilterAvailability(attribute, sortedItems.length > 0);
  }, [attribute, sortedItems.length, updateFilterAvailability]);

  const handleChange = (values: Iterable<string | number>) => {
    const newSet = new Set([...values].map(String));
    const currentSet = new Set(selectedValues);
    const toAdd = [...newSet].filter((v) => !currentSet.has(v));
    const toRemove = [...currentSet].filter((v) => !newSet.has(v));
    if (toAdd.length) {
      const filtersToAdd = toAdd.map((v) => `${attribute}:${v}`);
      addFilters?.(filtersToAdd);
    }
    if (toRemove.length) {
      removeFilters?.(toRemove.map((v) => `${attribute}:${v}`));
    }
  };

  const shouldEnableSearch = sortedItems.length > NUMBER_OF_ITEMS_TO_FIT_IN_FILTER_DROPDOWN;

  return (
    <div css={styles.wrapper} data-testid="filter-dropdown-productTypeDescription">
      <MultiSelect
        aria-label={_t('#{filterName} Filter', { filterName })}
        placeholder={filterName}
        searchable={shouldEnableSearch}
        searchProps={
          shouldEnableSearch
            ? {
                placeholder: _t('Search #{filterName}', { filterName }),
                'aria-label': _t('Search #{filterName}', { filterName }),
              }
            : undefined
        }
        value={selectedValues}
        onChange={handleChange}
      >
        {sortedItems.map((item) => (
          <Option key={String(item.value)}>{`${item.label} (${item.count})`}</Option>
        ))}
      </MultiSelect>
    </div>
  );
};

export default SearchFilterMultiSelect;
